import React, { useEffect, useState } from 'react'
import { redirect, useNavigate, useParams } from 'react-router-dom'
import { Alert, Button, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap' // Ajoutez Modal ici
import { Form, ToggleButton, ToggleButtonGroup } from 'react-bootstrap'
import { FaCopy, FaFlag, FaTrash } from 'react-icons/fa'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Helmet } from 'react-helmet'
import FullScreenImage from 'components/FullScreenImage'
import NotFound from 'components/NotFound'
import { toast } from 'react-toastify'
import { ToastOptionsDefault } from 'utils/toastOptions'

const ScreenshotView : React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id : string }>()
  const [screenshot, setScreenshot] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState<boolean>(false)
  const [isOwner, setIsOwner] = useState(false)
  const [title, setTitle] = useState('')
  const [baseUrl] = useState(process.env.NODE_ENV === 'development' ? 'http://localhost:3000': '')
  
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [selectedReason, setSelectedReason] = useState('')
  const [reportSubmitted, setReportSubmitted] = useState(false)
  const [disableReport, setDisableReport] = useState(false)
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const toggleDeleteModal = () => setDeleteModal(!deleteModal)
  const toggleReportModal = () => setReportModalOpen(!reportModalOpen)
  
  useEffect(() => {
    const fetchScreenshot = async () => {
      try {
        const token = localStorage.getItem('token')
        const headers : Record<string, string> = {
          'Content-Type': 'application/json',
        }
        
        if (token) {
          headers['Authorization'] = `Bearer ${ token }`
        }
        
        const response = await fetch(`/api/screenshot/${ id }`, { headers })
        if (!response.ok) {
          setNotFound(true)
          return
        }
        const data = await response.json()
        setScreenshot(data.screenshot)
        setTitle(data.screenshot.title)
        setIsOwner(data.screenshot.isOwner)
      } catch (err : any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    
    fetchScreenshot()
  }, [id])
  
  const handlePrivacyToggle = async () => {
    try {
      const response = await fetch(`/api/screenshot/${ screenshot.id }`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${ localStorage.getItem('token') }`,
        },
        body: JSON.stringify({ privacy: screenshot.privacy === 'public' ? 'private': 'public' }),
      })
      
      if (!response.ok) {
        toast.error('Failed to update privacy. Please retry later.', ToastOptionsDefault)
      } else {
        const updatedScreenshot = await response.json()
        setScreenshot(updatedScreenshot)
        toast.success(`Screenshot privacy has successfully been changed to ${ screenshot.privacy === 'public' ? 'private': 'public'}.`,
          ToastOptionsDefault)
      }
    } catch (err : any) {
      setError(err.message)
    }
  }
  
  const handleDelete = async () => {
    try {
      toggleDeleteModal()
      const response = await fetch(`/api/screenshot/${ screenshot.id }`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${ localStorage.getItem('token') }`,
        },
      })
      
      if (!response.ok) {
        toast.error('Failed to delete screenshot. Please retry later.', ToastOptionsDefault)
      } else {
        toast.warning('Screenshot has been successfully deleted.', {
          onClose: () => redirect('/gallery'),
          ...ToastOptionsDefault
        })
        navigate('/gallery', { replace: true })
      }
    } catch (err : any) {
      setError(err.message)
    }
  }
  
  const handleTitleChange = async () => {
    try {
      const response = await fetch(`/api/screenshot/${ screenshot.id }`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${ localStorage.getItem('token') }`,
        },
        body: JSON.stringify({ title }),
      })
      
      if (!response.ok) {
        toast.error('Failed to update screenshot title. Please retry later.', ToastOptionsDefault)
      } else {
        const updatedScreenshot = await response.json()
        setScreenshot(updatedScreenshot)
        toast.success('The title of your screenshot has been successfully changed.', ToastOptionsDefault)
      }
    } catch (err : any) {
      toast.error(`Unable to change the title of your screenshot : ${err.message}`, ToastOptionsDefault)
      setError(err.message)
    }
  }
  
  const handleReportSubmit = async () => {
    try {
      if (selectedReason === '') {
        return
      }
      const token = localStorage.getItem('token')
      const headers : Record<string, string> = {
        'Content-Type': 'application/json',
      }
      
      if (token) {
        headers['Authorization'] = `Bearer ${ token }`
      }
      const response = await fetch(`/api/screenshot/${ screenshot.id }/report`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: selectedReason }),
      })
      
      if (!response.ok) {
        toast.error('Failed to submit report. Please retry later.', ToastOptionsDefault)
      } else {
        setReportSubmitted(true)
        setDisableReport(true)
        toggleReportModal()
        toast.warning('Your report has been successfully submitted. We appreciate your contribution to maintaining the safety of Screen.me.',
          {
            ...ToastOptionsDefault,
            autoClose: 7000,
          })
      }
    } catch (err : any) {
      setError(err.message)
    }
  }
  
  if (loading) {
    return (
      <Container>
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Container>
    )
  }
  
  if (notFound) {
    return <NotFound/>
  }
  
  if (error) {
    return (
      <>
        <div className="position-relative">
          <section className="section section-hero section-shaped">
            <div className="shape shape-style-1 shape-default">
              <span className="span-150"/>
              <span className="span-50"/>
              <span className="span-50"/>
              <span className="span-75"/>
              <span className="span-100"/>
              <span className="span-75"/>
              <span className="span-50"/>
              <span className="span-100"/>
              <span className="span-50"/>
              <span className="span-100"/>
            </div>
            <Container className="mt-2">
              <Alert className="alert-danger" variant="danger">
                { error }
              </Alert>
            </Container>
            <div className="separator separator-bottom separator-skew zindex-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
                version="1.1"
                viewBox="0 0 2560 100"
                x="0"
                y="0"
              >
                <polygon className="fill-white" points="2560 0 2560 100 0 100"/>
              </svg>
            </div>
          </section>
        </div>
      </>
    )
  }
  
  return (
    <>
      <div className="position-relative">
        <section className="section section-hero section-shaped">
          <div className="shape shape-style-1 shape-default">
            <span className="span-150"/>
            <span className="span-50"/>
            <span className="span-50"/>
            <span className="span-75"/>
            <span className="span-100"/>
            <span className="span-75"/>
            <span className="span-50"/>
            <span className="span-100"/>
            <span className="span-50"/>
            <span className="span-100"/>
          </div>
          <Container fluid className="mt-2">
            <Helmet>
              <title>{ screenshot.title }</title>
              <meta property="og:title" content={ screenshot.title }/>
              <meta property="og:description" content={ `Screenshots viewed ${ screenshot.views } times` }/>
              <meta property="og:image" content={ `${ baseUrl }/${ screenshot.path }` }/>
              <meta property="og:url" content={ `https://screen-me.cloud/${ screenshot.url }` }/>
              <meta name="twitter:card" content="summary_large_image"/>
            </Helmet>
            <Row>
              <Col md={ 3 } className="bg-light p-3">
                <h1>
                  { isOwner ? (
                    <Form.Control
                      type="text"
                      value={ title }
                      onChange={ (e) => setTitle(e.target.value) }
                      onBlur={ handleTitleChange }
                    />
                  ): (
                    screenshot.title
                  ) }
                </h1>
                <p>
                  <b>Views:</b> { screenshot.views }
                </p>
                <p>
                  <b>Created At:</b>{ ' ' }
                  { new Date(screenshot.createdAt).toLocaleString('en-EN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }) }
                </p>
                <p>
                  <b>Author:</b> <a href={ '/user/' + screenshot.author }>{ screenshot.author }</a>
                </p>
                { isOwner ? (
                  <>
                    <div className="d-flex justify-content-between">
                      <CopyToClipboard text={ `${ 'https://screen-me.cloud/' + screenshot.url }` }>
                        <Button className="btn-icon btn-2 mb-3" color="primary" type="button"
                          onClick={() => {
                            toast.success('Screenshot URL copied to your clipboard !',
                              ToastOptionsDefault
                            )
                          }}>
                          <span className="btn-inner--icon">
                            <FaCopy/>
                          </span>
                        </Button>
                      </CopyToClipboard>
                      <ToggleButtonGroup
                        type="radio"
                        name="privacyOptions"
                        value={ screenshot.privacy }
                        onChange={ handlePrivacyToggle }
                        className="mb-2 w-100"
                      >
                        <ToggleButton id="public" variant="outline-secondary" value="public">
                          Public
                        </ToggleButton>
                        <ToggleButton id="private" variant="outline-secondary" value="private">
                          Private
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    <Button color="danger" onClick={ toggleDeleteModal } className="d-block w-100">
                      <FaTrash/> Delete
                    </Button>
                  </>
                ): (
                  <>
                    <Button color="danger" className=" w-100" onClick={ toggleReportModal }
                      disabled={ disableReport }>
                      <FaFlag /> Report
                    </Button>
                  </>
                ) }
              </Col>
              <Col md={ 9 } className="d-flex align-items-center justify-content-center">
                { screenshot && screenshot.path && (
                  <FullScreenImage src={ `${ baseUrl }/${ screenshot.path }` } alt={ screenshot.title }/>
                ) }
              </Col>
            </Row>
          </Container>
          <div className="separator separator-bottom separator-skew zindex-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon className="fill-white" points="2560 0 2560 100 0 100"/>
            </svg>
          </div>
        </section>
      </div>
      
      <Modal isOpen={ reportModalOpen } toggle={ toggleReportModal }>
        <ModalHeader toggle={ toggleReportModal }>Report Screenshot</ModalHeader>
        <ModalBody>
          <Form.Group>
            <Form.Label>Reason for Report</Form.Label>
            <Form.Control
              as="select"
              value={ selectedReason }
              onChange={ (e) => setSelectedReason(e.target.value) }
            >
              <option disabled selected value="">Select a reason</option>
              <option value="inappropriate_content">Inappropriate Content</option>
              <option value="copyright_violation">Copyright Violation</option>
              <option value="privacy_violation">Privacy Violation</option>
              <option value="hate_speech">Hate Speech</option>
              <option value="spam">Spam</option>
              <option value="other">Other</option>
            </Form.Control>
          </Form.Group>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="secondary" onClick={ toggleReportModal } className="col-md-5">
            Cancel
          </Button>
          <Button color="danger" onClick={ handleReportSubmit } className="col-md-5">
            <FaFlag/> Submit Report
          </Button>
        </ModalFooter>
      </Modal>
      
      <Modal isOpen={ reportSubmitted } toggle={ () => setReportSubmitted(false) }>
        <ModalHeader toggle={ () => setReportSubmitted(false) }>Report Submitted</ModalHeader>
        <ModalBody>
          Thank you for your report. We will review the content as soon as possible.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={ () => setReportSubmitted(false) }>
            Close
          </Button>
        </ModalFooter>
      </Modal>
      
      <Modal isOpen={ deleteModal } toggle={ toggleDeleteModal }>
        <ModalHeader toggle={ toggleDeleteModal }>
          <h2>Confirm Delete Screenshot</h2>
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to delete this screenshot ?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={() => {
            if (screenshot.id) {
              handleDelete()
            }
          }}>
            Confirm Delete
          </Button>{ ' ' }
          <Button color="secondary" onClick={ toggleDeleteModal }>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ScreenshotView
